import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { BsCameraVideo } from 'react-icons/bs';
import { FiSave, FiRotateCcw, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import { createVideo } from '../../../../store/videos/videosSlice';
import axiosInstance from '../../../../utilities/axios';

const CATEGORIES = ['عام', 'حروب', 'زراعة', 'نقاشات', 'مراجعات', 'تعليم'];

const Page = styled.div`
  padding: 28px 24px;
  max-width: 760px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  .icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(147,51,234,0.12); border: 1px solid rgba(147,51,234,0.25);
    display: flex; align-items: center; justify-content: center;
    svg { color: #a78bfa; font-size: 20px; }
  }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;

const Card = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 28px 24px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  label {
    display: block; font-size: 12px; font-weight: 600; color: #9ca3af;
    margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em;
  }
  input, select, textarea {
    width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; padding: 10px 13px; color: #e2e8f0; font-size: 13.5px;
    font-family: inherit; outline: none; transition: border-color 0.2s;
    &::placeholder { color: #374151; }
    &:focus { border-color: rgba(147,51,234,0.5); }
  }
  select option { background: #1A1A1A; }
  .err { font-size: 12px; color: #f87171; margin-top: 5px; }
  .hint { font-size: 11.5px; color: #6b7280; margin-top: 5px; }
`;

const Divider = styled.div`
  height: 1px; background: rgba(255,255,255,0.06); margin: 24px 0;
`;

const BtnRow = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 28px;
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 9px; border: none;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s;
  opacity: ${p => p.disabled ? 0.55 : 1};
  background: ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.12)' : '#7C3AED'};
  color: ${p => p.variant === 'reset' ? '#f87171' : '#fff'};
  border: 1px solid ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.3)' : 'transparent'};
  &:hover { opacity: 0.85; }
`;

const AlertBox = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 20px;
  svg { color: #f87171; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fca5a5; }
`;

const InfoBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.2);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 24px;
  svg { color: #fbbf24; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fde68a; line-height: 1.5; }
`;

const PreviewBox = styled.div`
  display: flex; align-items: center; gap: 12px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 9px; padding: 10px 12px; margin-bottom: 20px;
  img { width: 96px; border-radius: 6px; object-fit: cover; }
  .title { font-size: 13px; font-weight: 700; color: #e2e8f0; margin-bottom: 6px; }
  .stats { display: flex; gap: 12px; font-size: 12px; color: #9ca3af; }
`;

const SuccessBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: 48px 24px; text-align: center;
  svg { font-size: 52px; color: #34d399; }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 14px; color: #6b7280; max-width: 360px; }
`;

const AddVideo = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const videos = useSelector(state => state.videos);
  const [submitted, setSubmitted] = useState(false);
  const [fetchingStats, setFetchingStats] = useState(false);
  const [fetchedStats, setFetchedStats] = useState(null);
  const debounceRef = useRef(null);

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
    defaultValues: { category: 'عام' }
  });

  const youtubeUrl = watch('youtube_url');

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFetchedStats(null);
    setValue('views', '0');
    setValue('likes', '0');
    setValue('comments', '0');
    setValue('duration', '0:00');
    if (!youtubeUrl || !youtubeUrl.includes('youtu')) return;
    debounceRef.current = setTimeout(async () => {
      setFetchingStats(true);
      try {
        const res = await axiosInstance.get(`/api/fetch_youtube_stats?url=${encodeURIComponent(youtubeUrl)}`, {
          headers: { Authorization: auth.token }
        });
        if (res.data.success) {
          const { title, creator, thumbnail, creator_avatar, views, likes, comments, duration } = res.data.data;
          if (title) setValue('title', title);
          if (creator) setValue('creator', creator);
          setValue('youtube_thumbnail_url', thumbnail || '');
          setValue('youtube_creator_avatar_url', creator_avatar || '');
          setValue('views', views);
          setValue('likes', likes);
          setValue('comments', comments);
          setValue('duration', duration || '0:00');
          setFetchedStats({ title, creator, thumbnail, creator_avatar, views, likes, comments, duration });
        }
      } catch (_) {
        // silently ignore
      } finally {
        setFetchingStats(false);
      }
    }, 800);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeUrl, setValue, auth.token]);

  if (!auth.isAuth) return <Navigate to="/login" />;

  const onSubmit = (values) => {
    const fd = new FormData();
    Object.entries(values).forEach(([k, v]) => {
      if (k === 'thumbnail' && v?.[0]) fd.append('thumbnail', v[0]);
      else if (v !== undefined && v !== null) fd.append(k, v);
    });
    dispatch(createVideo(fd)).unwrap().then(() => {
      setSubmitted(true);
    });
  };

  return (
    <WebsiteLayout>
      <Page>
        <PageHeader>
          <div className="icon"><BsCameraVideo /></div>
          <div>
            <div className="title">{t('website.add_video.title', 'إضافة فيديو')}</div>
            <div className="sub">{t('website.add_video.sub', 'شارك فيديو يوتيوب وانتظر موافقة الإدارة')}</div>
          </div>
        </PageHeader>

        <Card>
          {submitted ? (
            <SuccessBox>
              <FiCheckCircle />
              <div className="title">{t('website.add_video.success_title', 'تم إرسال طلبك بنجاح!')}</div>
              <div className="sub">
                {t('website.add_video.success_sub', 'فيديوك قيد المراجعة، سيظهر في القائمة بعد موافقة الإدارة.')}
              </div>
              <Btn onClick={() => navigate('/videos')} style={{ marginTop: 8 }}>
                {t('website.add_video.go_videos', 'عرض الفيديوهات')}
              </Btn>
            </SuccessBox>
          ) : (
            <>
              <InfoBanner>
                <FiAlertCircle />
                <div className="msg">
                  {t('website.add_video.pending_note', 'فيديوك سيظهر في القائمة بعد مراجعة الإدارة والموافقة عليه.')}
                </div>
              </InfoBanner>

              {videos.errors.length > 0 && (
                <AlertBox>
                  <FiAlertCircle />
                  <div className="msg">{videos.errors.map((e, i) => <div key={i}>{e?.msg}</div>)}</div>
                </AlertBox>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <input type="hidden" {...register('youtube_thumbnail_url')} />
                <input type="hidden" {...register('youtube_creator_avatar_url')} />
                <input type="hidden" {...register('views')} />
                <input type="hidden" {...register('likes')} />
                <input type="hidden" {...register('comments')} />
                <input type="hidden" {...register('duration')} />

                <FormGroup>
                  <label>{t('website.add_video.youtube_url', 'رابط يوتيوب')}</label>
                  <input placeholder="https://www.youtube.com/watch?v=..."
                    {...register('youtube_url', { required: t('validation.required', 'مطلوب') })} />
                  {errors.youtube_url && <div className="err">{errors.youtube_url.message}</div>}
                  {fetchingStats && <div className="hint">{t('website.add_video.fetching', 'جاري جلب بيانات الفيديو...')}</div>}
                </FormGroup>

                {fetchedStats && !fetchingStats && (
                  <PreviewBox>
                    {fetchedStats.thumbnail && <img src={fetchedStats.thumbnail} alt={fetchedStats.title} />}
                    <div>
                      {fetchedStats.title && <div className="title">{fetchedStats.title}</div>}
                      {fetchedStats.creator && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
                          {fetchedStats.creator_avatar && (
                            <img src={fetchedStats.creator_avatar} alt={fetchedStats.creator}
                              style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }} />
                          )}
                          {fetchedStats.creator}
                        </div>
                      )}
                      <div className="stats">
                        <span>👁 {fetchedStats.views}</span>
                        <span>👍 {fetchedStats.likes}</span>
                        <span>💬 {fetchedStats.comments}</span>
                        {fetchedStats.duration && <span>⏱ {fetchedStats.duration}</span>}
                      </div>
                    </div>
                  </PreviewBox>
                )}

                <Row>
                  <FormGroup>
                    <label>{t('website.add_video.video_title', 'عنوان الفيديو')}</label>
                    <input placeholder="عنوان الفيديو" {...register('title', { required: t('validation.required', 'مطلوب') })} />
                    {errors.title && <div className="err">{errors.title.message}</div>}
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_video.creator', 'صانع المحتوى')}</label>
                    <input placeholder="اسم صانع المحتوى" {...register('creator', { required: t('validation.required', 'مطلوب') })} />
                    {errors.creator && <div className="err">{errors.creator.message}</div>}
                  </FormGroup>
                </Row>

                <Divider />

                <Row>
                  <FormGroup>
                    <label>{t('website.add_video.category', 'التصنيف')}</label>
                    <select {...register('category')}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_video.thumbnail', 'صورة مصغرة (اختياري)')}</label>
                    <input type="file" accept="image/*" {...register('thumbnail')} style={{ padding: '6px' }} />
                    <div className="hint">{t('website.add_video.thumbnail_hint', 'تُجلب تلقائياً من يوتيوب إن تُركت فارغة')}</div>
                  </FormGroup>
                </Row>

                <BtnRow>
                  <Btn type="button" variant="reset" onClick={() => reset()} disabled={videos.isLoading}>
                    <FiRotateCcw /> {t('website.add_video.reset', 'مسح')}
                  </Btn>
                  <Btn type="submit" disabled={videos.isLoading}>
                    <FiSave /> {videos.isLoading ? t('website.add_video.saving', 'جاري الإرسال...') : t('website.add_video.submit', 'إرسال الطلب')}
                  </Btn>
                </BtnRow>
              </form>
            </>
          )}
        </Card>
      </Page>
    </WebsiteLayout>
  );
};

export default AddVideo;
